import { BaseVehicleProduct } from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/BaseVehicleProduct";
import {
  AkaHouseContentsCoverage,
  HouseContentsCoverage,
  InsuranceCoverage,
} from "Store/interfaces/products/coverage";

import { Option } from "@storeblocks/select";
import { DEFAULT_DEDUCTIBLE_HOUSE_CONTENTS } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/HouseContents/houseContentsConstants";
import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { VALUE_TYPE } from "Components/NumberValue";
import {
  DEDUCTIBLE_OLD_EIGHT_THOUSAND_VALUE,
  DEDUCTIBLE_OLD_FOUR_THOUSAND_VALUE,
  DEDUCTIBLE_OLD_SIXTEEN_THOUSAND_VALUE,
  DEDUCTIBLE_OLD_SIX_THOUSAND_VALUE,
  DEDUCTIBLE_OLD_THIRTY_THOUSAND_VALUE,
  DEDUCTIBLE_OLD_TWELVE_THOUSAND_VALUE,
  DEDUCTIBLE_OLD_TWELVE_THOUSAND_VALUE_CARAVAN,
} from "Constants/deductibles";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { CalculationPrice } from "Store/priceCalculation/priceCalculationInterfaces";
import { getPriceFormatFromValue } from "../formatting";
import { sum } from "./arrayUtils";
import { isDefined } from "./functions";
import { isManualProduct } from "./productUtils";

export const MIN_DISCOUNT_VALUE = 12;
const DEDUCTIBLE_THREE_THOUSAND = "3000";
export const DEDUCTIBLE_FOUR_THOUSAND = "4000";

export interface PriceSummary {
  priceMonth: number;
  priceYear: number;
  discountYear: number;
  trafikkforsikringsavgiftYear: number;
  trafikkforsikringsavgiftMonth: number;
  coInsuredPriceMonth: number;
}

export const priceMapFrom = (
  coverages: CoveragePriceCalculation[],
): Map<string, CalculationPrice | undefined> => {
  const prices = new Map<string, CalculationPrice | undefined>();
  coverages.forEach(([coverage, priceCalculationEntry]) => {
    prices.set(
      coverage,
      priceCalculationEntry.prices && priceCalculationEntry.prices.length > 0
        ? priceCalculationEntry.prices[0]
        : undefined,
    );
  });
  return prices;
};

export const createPriceSummary = (
  products: InsuranceProduct[],
): PriceSummary => {
  let priceMonth = 0;
  let priceYear = 0;
  let discountYear = 0;
  let trafikkforsikringsavgiftYear = 0;
  let trafikkforsikringsavgiftMonth = 0;
  let coInsuredPriceMonth = 0;

  products.forEach((product) => {
    const { price } = product?.priceSpecifications;
    if (price) {
      if (
        Number(price.perYearFullPrecise) &&
        Number(price.perYearFullPrecise) > Number(price.perYearPrecise)
      ) {
        discountYear += Number(price.yearlyDiscount);
      }
      priceMonth += roundedPrice(price.perMonthPrecise);
      priceYear += Number(price.perYearPrecise);
      if (price.coInsuredMonthlyPricePrecise) {
        coInsuredPriceMonth += Number(price.coInsuredMonthlyPricePrecise);
      }
    }

    const productName = isManualProduct(product)
      ? product.insuranceName
      : product.name;

    switch (productName) {
      case INSURANCE_NAME_VALUE.CAR:
      case INSURANCE_NAME_VALUE.CAR_NORSK_ELBILFORENING:
      case INSURANCE_NAME_VALUE.MOPED:
      case INSURANCE_NAME_VALUE.MOTORCYCLE:
      case INSURANCE_NAME_VALUE.TRACTOR:
      case INSURANCE_NAME_VALUE.VETERAN_AUTO:
        const { trafikkforsikringsavgift } = product as BaseVehicleProduct;
        trafikkforsikringsavgiftYear += Number(
          trafikkforsikringsavgift?.yearlyAmount,
        );
        trafikkforsikringsavgiftMonth += Number(
          trafikkforsikringsavgift?.monthlyAmount,
        );
        break;
      default:
        break;
    }
  });

  return {
    priceMonth,
    priceYear,
    discountYear,
    trafikkforsikringsavgiftYear,
    trafikkforsikringsavgiftMonth,
    coInsuredPriceMonth,
  };
};

export const getDiscountYear = (
  activeProducts: InsuranceProduct[],
  adviceProducts: InsuranceProduct[],
): number => {
  const { discountYear } = createPriceSummary([
    ...activeProducts,
    ...adviceProducts,
  ]);
  return discountYear;
};

export const getPricePerMonth = (
  calculationPrice: CalculationPrice,
): number => {
  return roundedPrice(calculationPrice.monthlyPricePrecise);
};

export const getPricePerYear = (calculationPrice: CalculationPrice): number => {
  return roundedPrice(calculationPrice.yearlyPricePrecise);
};

export const roundedPrice = (value: number | undefined): number => {
  return Math.round(Number(value));
};

export const pricePerMonth = (product: InsuranceProduct): number => {
  return Number(product.priceSpecifications.price.perMonthPrecise);
};

export const tfaPerMonth = (product: InsuranceProduct): number => {
  const { trafikkforsikringsavgift } = product as BaseVehicleProduct;
  return Number(trafikkforsikringsavgift?.monthlyAmount ?? 0);
};

export const fullPricePerMonth = (product: InsuranceProduct): number => {
  return Number(product.priceSpecifications.price.perMonthFullPrecise);
};

export const oneTimePrice = (product: InsuranceProduct): number => {
  return Number(product.priceSpecifications.price.perYearPrecise);
};

export const getYearlyDiscount = (price?: CalculationPrice): number => {
  if (
    price?.fullYearlyPricePrecise &&
    price.fullYearlyPricePrecise > price.yearlyPricePrecise
  ) {
    return price.fullYearlyPricePrecise - price.yearlyPricePrecise;
  }
  return 0;
};

export const getMonthlyDiscount = (price: CalculationPrice): number => {
  if (
    price.fullMonthlyPricePrecise &&
    price.fullMonthlyPricePrecise > price.monthlyPricePrecise
  ) {
    return roundedPrice(
      price.fullMonthlyPricePrecise - price.monthlyPricePrecise,
    );
  }
  return 0;
};

export const getDeductibleOptions = (
  insuranceName: INSURANCE_NAME_VALUE,
  coverages: CoveragePriceCalculation[],
): Option[] => {
  if (coverages.length === 0) {
    return [];
  }
  return coverages[0][1].prices
    .filter((price) =>
      filterSoonToBeDeprecatedDeductibles(insuranceName, price.code),
    )
    .map((price) => {
      const value = price.value ?? "";
      return {
        label: getPriceFormatFromValue(value, VALUE_TYPE.NOK),
        value,
      };
    })
    .filter((option) => option.value)
    .sort((a, b) => Number(a.value) - Number(b.value));
};

// TODO - SSX-2297: To be removed when these codes are no longer in use in production
export const filterSoonToBeDeprecatedDeductibles = (
  productName: INSURANCE_NAME_VALUE,
  code: string | undefined,
): boolean => {
  const listOfAffectedProductNames = [
    INSURANCE_NAME_VALUE.BOAT,
    INSURANCE_NAME_VALUE.CARAVAN,
    INSURANCE_NAME_VALUE.CABIN,
    INSURANCE_NAME_VALUE.CAR,
    INSURANCE_NAME_VALUE.HOUSE,
    INSURANCE_NAME_VALUE.MOTORCYCLE,
  ];
  const isSoonToBeDeprecatedDeductible = [
    DEDUCTIBLE_OLD_FOUR_THOUSAND_VALUE,
    DEDUCTIBLE_OLD_SIX_THOUSAND_VALUE,
    DEDUCTIBLE_OLD_EIGHT_THOUSAND_VALUE,
    DEDUCTIBLE_OLD_TWELVE_THOUSAND_VALUE,
    DEDUCTIBLE_OLD_TWELVE_THOUSAND_VALUE_CARAVAN,
    DEDUCTIBLE_OLD_SIXTEEN_THOUSAND_VALUE,
    DEDUCTIBLE_OLD_THIRTY_THOUSAND_VALUE,
  ].includes(code ?? "");

  if (listOfAffectedProductNames.includes(productName)) {
    return !isSoonToBeDeprecatedDeductible;
  }

  return true;
};

export function getDefaultDeductibleForProduct(key: string): string {
  switch (key) {
    case HouseContentsCoverage.HOUSE_CONTENTS_STANDARD:
    case HouseContentsCoverage.HOUSE_CONTENTS_EXTENDED:
      return DEFAULT_DEDUCTIBLE_HOUSE_CONTENTS;
    case AkaHouseContentsCoverage.AKA_HOUSE_CONTENTS_STANDARD:
      return DEDUCTIBLE_THREE_THOUSAND;
    default:
      return DEDUCTIBLE_FOUR_THOUSAND;
  }
}

const getOwnRiskPrice = (
  ownRiskPrices: CalculationPrice[],
  ownRisk?: string,
): CalculationPrice => {
  if (isDefined(ownRisk)) {
    const ownRiskPrice = ownRiskPrices.find(
      (ownRiskPrice) => ownRiskPrice.value === ownRisk,
    );
    if (isDefined(ownRiskPrice)) {
      return ownRiskPrice;
    }
  }
  return ownRiskPrices[0];
};

export function getSelectedCoveragesDiscount(
  selectedCoverages: InsuranceCoverage[],
  coveragePrices: CoveragePriceCalculation[],
  ownRisk?: string,
  defaultOwnRisk?: string,
): number {
  return coveragePrices
    .filter(([coverage]) => selectedCoverages.includes(coverage))
    .map(([, priceEntry]) => priceEntry.prices)
    .map((prices) => getOwnRiskPrice(prices, ownRisk ?? defaultOwnRisk))
    .map(getYearlyDiscount)
    .reduce(sum, 0);
}
